/**
 * Optimization Panel — Two modes: Quick and Standard.
 * Quick: GeographicMDS + VROOM + TSPPolish (~30s)
 * Standard: DecisionTree auto-selects best pipeline (14 min)
 */

import React, { useState, useEffect, useCallback, useRef } from 'react';
import * as api from '../services/routeOptimizerService';
import { useJobPolling } from '../hooks/useJobPolling';
import { TIER_ORDER, TIER_LABELS } from '../utils/constants';

/** Format elapsed seconds as M:SS (e.g. "20:50") or "45s" for short durations. */
function formatElapsed(seconds) {
  if (seconds == null) return '-';
  const s = Math.round(seconds);
  if (s < 60) return `${s}s`;
  const m = Math.floor(s / 60);
  const rem = s % 60;
  return `${m}:${String(rem).padStart(2, '0')}`;
}

export default function OptimizationPanel({ weekId, details, onRefresh, onProceed }) {
  const [buildingMatrix, setBuildingMatrix] = useState(false);
  const [optimizing, setOptimizing] = useState(false);
  const [activeJobPk, setActiveJobPk] = useState(null); // Track specific job ID
  const [tierSummary, setTierSummary] = useState({});
  const [concurrentError, setConcurrentError] = useState('');
  const [feasibility, setFeasibility] = useState(null);
  const isSubmittingRef = useRef(false); // Synchronous guard against rapid double-clicks

  const onRefreshRef = useRef(onRefresh);
  useEffect(() => { onRefreshRef.current = onRefresh; }, [onRefresh]);

  // On mount: detect in-progress or pending jobs
  useEffect(() => {
    const checkRunningJobs = async () => {
      try {
        const res = await api.getOptimizeStatus(weekId);
        if (res.status === 'pending' || res.status === 'in_progress') {
          setOptimizing(true);
        }
      } catch (err) {
        console.warn('[OptPanel] optimize status check failed:', err);
      }
      try {
        const res = await api.getBuildMatrixStatus(weekId);
        if (res.status === 'pending' || res.status === 'in_progress') {
          setBuildingMatrix(true);
        }
      } catch (err) {
        console.warn('[OptPanel] matrix status check failed:', err);
      }
    };
    checkRunningJobs();
    api.getFeasibility(weekId).then(data => { if (data) setFeasibility(data); });
  }, [weekId]);

  // Load tier summary on mount, when details change, and periodically while optimizing
  const refreshSummary = useCallback(() => {
    api.getResultsSummary(weekId)
      .then(data => setTierSummary(data.tiers || {}))
      .catch(() => {});
  }, [weekId]);

  useEffect(() => { refreshSummary(); }, [refreshSummary, details.result_tiers]);

  useEffect(() => {
    if (!optimizing) return;
    const id = setInterval(refreshSummary, 10000);
    return () => clearInterval(id);
  }, [optimizing, refreshSummary]);

  const matrixPoll = useJobPolling(
    useCallback(() => api.getBuildMatrixStatus(weekId), [weekId]),
    buildingMatrix, 2000
  );

  const optimizePoll = useJobPolling(
    useCallback(() => api.getOptimizeStatus(weekId, activeJobPk), [weekId, activeJobPk]),
    optimizing, 2000
  );

  useEffect(() => {
    if (matrixPoll.status === 'complete' || matrixPoll.status === 'failed') {
      if (buildingMatrix) {
        setBuildingMatrix(false);
        onRefreshRef.current();
      }
    }
  }, [matrixPoll.status, matrixPoll.progress, buildingMatrix]);

  useEffect(() => {
    if (optimizePoll.status === 'complete' || optimizePoll.status === 'failed') {
      if (optimizing) {
        const pendingLeft = optimizePoll.data?.pending_jobs || 0;
        if (pendingLeft > 0) {
          refreshSummary();
          onRefreshRef.current();
        } else {
          setOptimizing(false);
          refreshSummary();
          onRefreshRef.current();
        }
      }
    }
    if (optimizePoll.status === 'idle' && optimizing) {
      setOptimizing(false);
      refreshSummary();
      onRefreshRef.current();
    }
  }, [optimizePoll.status, optimizePoll.progress, optimizePoll.data, optimizing, refreshSummary]);

  const handleBuildMatrix = async () => {
    if (isSubmittingRef.current) return; // Prevent rapid double-clicks
    isSubmittingRef.current = true;
    setConcurrentError('');
    try {
      await api.buildMatrix(weekId);
      setBuildingMatrix(true);
    } catch (err) {
      isSubmittingRef.current = false;
      if (err.response?.status === 409) {
        const msg = err.response.data?.error || 'Another job is already running for this branch.';
        setConcurrentError(msg);
      } else {
        console.error('Build matrix failed:', err);
      }
    }
  };

  const handleOptimize = async (mode) => {
    if (isSubmittingRef.current) return; // Prevent rapid double-clicks
    isSubmittingRef.current = true;
    setConcurrentError('');
    try {
      const res = await api.optimize(weekId, { mode });
      const jobPk = res?.jobs?.[0]?.job_pk || null;
      setActiveJobPk(jobPk);
      _maxStageReached = 0; // Reset progress stages for new optimization
      setOptimizing(true);
    } catch (err) {
      isSubmittingRef.current = false; // Allow retry on error
      if (err.response?.status === 409) {
        const msg = err.response.data?.error || 'Another optimization is already running for this branch.';
        setConcurrentError(msg);
      } else {
        console.error('Optimize failed:', err);
        setConcurrentError(err.message || 'Optimization failed');
      }
    }
  };

  const canBuildMatrix = details.address_count > 0 && !details.has_distance_matrix;
  const canOptimize = details.address_count > 0 && details.worker_count > 0;

  const completedTiers = TIER_ORDER.filter(t => tierSummary[t]);
  const bestTier = completedTiers.length > 0
    ? completedTiers.reduce((best, t) =>
        (tierSummary[t].total_distance_miles < tierSummary[best].total_distance_miles) ? t : best
      )
    : null;

  const buttonLabel = optimizing
    ? (optimizePoll.data?.tier
        ? `Running ${TIER_LABELS[optimizePoll.data.tier] || ''}...`
        : 'Optimizing...')
    : null;

  return (
    <div className="ro-panel">
      <h3>Distance Matrix</h3>
      {details.has_distance_matrix ? (
        <div className="ro-inline-row">
          <span className="ro-status-check">{'\u2713'}</span>
          <span className="ro-success">Distance matrix is ready ({details.address_count} addresses).</span>
        </div>
      ) : (
        <>
          <p className="ro-form-description">
            Build a distance matrix using real road distances. This is required before optimization.
          </p>
          <button
            className="ro-btn ro-btn-primary"
            onClick={handleBuildMatrix}
            disabled={!canBuildMatrix || buildingMatrix}
          >
            {buildingMatrix ? 'Building...' : 'Build Distance Matrix'}
          </button>
          {!details.address_count && <p className="ro-warning">Upload addresses first.</p>}
          {details.address_count > 0 && details.has_distance_matrix === false && !buildingMatrix && (
            <p className="ro-form-hint">
              Matrix will also auto-build when you start optimization.
            </p>
          )}
        </>
      )}

      {buildingMatrix && (
        <div className="ro-progress">
          <div className="ro-progress-text">{matrixPoll.progress || 'Starting...'}</div>
          {matrixPoll.error && <div className="ro-error">{matrixPoll.error}</div>}
        </div>
      )}

      {/* Settings overview (read-only) */}
      {details.settings && (
        <>
          <hr />
          <h4 className="ro-subsection-title">Settings Overview</h4>
          <div className="ro-settings-overview">
            <div className="ro-setting-chip">
              <span className="ro-setting-chip-label">Workdays</span>
              <span className="ro-setting-chip-value">{(details.settings.workdays || []).map(d => d.substring(0, 3)).join(', ') || '\u2014'}</span>
            </div>
            <div className="ro-setting-chip">
              <span className="ro-setting-chip-label">Shift</span>
              <span className="ro-setting-chip-value">{details.settings.shift_hours ?? 8}h (ends {details.settings.shift_end_time ?? '16:30'})</span>
            </div>
            <div className="ro-setting-chip">
              <span className="ro-setting-chip-label">Service</span>
              <span className="ro-setting-chip-value">{details.settings.service_minutes ?? 20} min/stop</span>
            </div>
            {details.settings.complex_service_minutes && (
              <div className="ro-setting-chip">
                <span className="ro-setting-chip-label">Complex</span>
                <span className="ro-setting-chip-value">{details.settings.complex_service_minutes} min/stop</span>
              </div>
            )}
            <div className="ro-setting-chip">
              <span className="ro-setting-chip-label">Depot</span>
              <span className="ro-setting-chip-value">{details.settings.depot_lat?.toFixed(4)}, {details.settings.depot_lon?.toFixed(4)}</span>
            </div>
            <div className={`ro-setting-chip${!details.settings.lunch_break_duration_minutes ? ' ro-setting-chip-muted' : ''}`}>
              <span className="ro-setting-chip-label">Lunch</span>
              <span className="ro-setting-chip-value">
                {details.settings.lunch_break_duration_minutes
                  ? `${details.settings.lunch_break_duration_minutes} min (${details.settings.lunch_break_earliest ?? '11:00'}\u2013${details.settings.lunch_break_latest ?? '14:00'})`
                  : 'Not configured'}
              </span>
            </div>
            <div className={`ro-setting-chip${!(details.settings.chem_refill_lat && details.settings.chem_refill_lon) ? ' ro-setting-chip-muted' : ''}`}>
              <span className="ro-setting-chip-label">Chem Refill</span>
              <span className="ro-setting-chip-value">
                {details.settings.chem_refill_lat && details.settings.chem_refill_lon
                  ? (details.settings.chem_refill_address
                      ? (details.settings.chem_refill_address.length > 30
                          ? details.settings.chem_refill_address.slice(0, 27) + '...'
                          : details.settings.chem_refill_address)
                      : `${details.settings.chem_refill_lat.toFixed(4)}, ${details.settings.chem_refill_lon.toFixed(4)}`)
                    + (details.settings.chem_refill_duration_minutes ? ` (${details.settings.chem_refill_duration_minutes} min)` : '')
                  : 'Not configured'}
              </span>
            </div>
            {details.settings.max_stops_per_day && (
              <div className="ro-setting-chip">
                <span className="ro-setting-chip-label">Max Stops/Day</span>
                <span className="ro-setting-chip-value">{details.settings.max_stops_per_day}</span>
              </div>
            )}
            <div className="ro-setting-chip">
              <span className="ro-setting-chip-label">Distance Method</span>
              <span className="ro-setting-chip-value">Road distances</span>
            </div>
            <div className="ro-setting-chip">
              <span className="ro-setting-chip-label">Change Lock</span>
              <span className="ro-setting-chip-value">{details.settings.change_lock_days ?? 90} days</span>
            </div>
            <div className="ro-setting-chip">
              <span className="ro-setting-chip-label">Optimization Priority</span>
              <span className="ro-setting-chip-value">Minimize drive time</span>
            </div>
            <div className="ro-setting-chip">
              <span className="ro-setting-chip-label">Service Time</span>
              <span className="ro-setting-chip-value">
                {details.settings.complex_service_minutes
                  ? `${details.settings.service_minutes ?? 20} min standard, ${details.settings.complex_service_minutes} min complex`
                  : `${details.settings.service_minutes ?? 20} min/stop (branch default)`}
              </span>
            </div>
          </div>
        </>
      )}

      {/* Feasibility Report */}
      {feasibility && (
        <>
          <hr />
          <h4 className="ro-subsection-title">Constraint Analysis</h4>
          <div className={`ro-feasibility-banner ro-feasibility-${feasibility.verdict}`}>
            <strong>{feasibility.verdict_text || feasibility.verdict}</strong>
          </div>
          {feasibility.issues && (
            <div className="ro-feasibility-issues">
              {(feasibility.issues.impossible || []).length > 0 && (
                <div className="ro-feasibility-section ro-feasibility-impossible">
                  <strong>Impossible ({feasibility.issues.impossible.length})</strong>
                  <ul>{feasibility.issues.impossible.map((issue, i) => (
                    <li key={i}><strong>{issue.type}:</strong> {issue.detail}{issue.suggestion ? <> &mdash; <em>{issue.suggestion}</em></> : ''}</li>
                  ))}</ul>
                </div>
              )}
              {(feasibility.issues.critical || []).length > 0 && (
                <div className="ro-feasibility-section ro-feasibility-critical">
                  <strong>Critical ({feasibility.issues.critical.length})</strong>
                  <ul>{feasibility.issues.critical.map((issue, i) => (
                    <li key={i}><strong>{issue.type}:</strong> {issue.detail}{issue.suggestion ? <> &mdash; <em>{issue.suggestion}</em></> : ''}</li>
                  ))}</ul>
                </div>
              )}
              {(feasibility.issues.warning || []).length > 0 && (
                <div className="ro-feasibility-section ro-feasibility-warning">
                  <strong>Warnings ({feasibility.issues.warning.length})</strong>
                  <ul>{feasibility.issues.warning.map((issue, i) => (
                    <li key={i}><strong>{issue.type}:</strong> {issue.detail}</li>
                  ))}</ul>
                </div>
              )}
            </div>
          )}
        </>
      )}

      <hr />

      <h3>Route Optimization</h3>

      {concurrentError && (
        <div className="ro-message-error" style={{ marginBottom: '1rem' }}>
          {concurrentError}
          <div style={{ marginTop: '0.5rem', fontSize: '0.85rem', opacity: 0.85 }}>
            Please try again shortly, or wait for the current job to finish.
          </div>
        </div>
      )}

      <div className="ro-batch-controls ro-section" style={{ display: 'flex', gap: '0.75rem' }}>
        <button
          className="ro-btn"
          onClick={() => handleOptimize('quick')}
          disabled={!canOptimize || optimizing}
          style={{ flex: 1 }}
        >
          {optimizing && optimizePoll.data?.tier === 'quick' ? 'Running Quick...' : 'Quick Optimization'}
        </button>
        <button
          className="ro-btn ro-btn-primary"
          onClick={() => handleOptimize('standard')}
          disabled={!canOptimize || optimizing}
          style={{ flex: 1 }}
        >
          {optimizing && optimizePoll.data?.tier === 'standard' ? 'Running Standard...' : 'Standard Optimization'}
        </button>
      </div>

      {!canOptimize && (
        <p className="ro-warning">
          Need: addresses ({details.address_count ?? 0}),
          workers ({details.worker_count ?? 0}),
          matrix (auto-built if missing)
        </p>
      )}

      {optimizing && (
        <div className="ro-progress">
          <ProgressStages progress={optimizePoll.progress} />
          {optimizePoll.error && <div className="ro-error">{optimizePoll.error}</div>}
        </div>
      )}

      {/* Retry on failure */}
      {!optimizing && optimizePoll.status === 'failed' && (
        <div>
          <div className="ro-message-error" style={{ marginBottom: '0.5rem' }}>
            {optimizePoll.error || 'Optimization failed'}
          </div>
          <button
            className="ro-btn ro-btn-danger ro-btn-sm"
            onClick={() => handleOptimize('standard')}
          >
            Retry Optimization
          </button>
        </div>
      )}

      {/* Results Comparison Table */}
      {completedTiers.length > 0 && (
        <>
          <hr />
          <h3>Results Comparison</h3>
          <table className="ro-preview-table" style={{ tableLayout: 'fixed', width: '100%' }}>
            <colgroup>
              <col style={{ width: '100px' }} />
              <col style={{ width: '80px' }} />
              {completedTiers.length > 1 && <col style={{ width: '100px' }} />}
              <col style={{ width: '65px' }} />
              <col style={{ width: '55px' }} />
              <col style={{ width: '55px' }} />
              <col style={{ width: '60px' }} />
              <col style={{ width: '50px' }} />
            </colgroup>
            <thead>
              <tr>
                <th>Mode</th>
                <th style={{ textAlign: 'right' }}>Miles</th>
                {completedTiers.length > 1 && <th style={{ textAlign: 'right' }}>Savings</th>}
                <th style={{ textAlign: 'right' }}>Routes</th>
                <th style={{ textAlign: 'right' }}>Employees</th>
                <th style={{ textAlign: 'right' }}>Stops</th>
                <th style={{ textAlign: 'right' }}>Time</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {completedTiers.map((tier, idx) => {
                const s = tierSummary[tier];
                const isBest = tier === bestTier;
                const baseMiles = tierSummary[completedTiers[0]]?.total_distance_miles;
                const diff = idx > 0 && baseMiles ? s.total_distance_miles - baseMiles : null;
                return (
                  <tr key={tier} className={isBest ? 'ro-row-best' : ''}>
                    <td>{TIER_LABELS[tier] || tier}</td>
                    <td style={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{s.total_distance_miles?.toFixed(1)}</td>
                    {completedTiers.length > 1 && (
                      <td style={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums' }} className={diff != null && diff < 0 ? 'ro-cmp-positive' : diff > 0 ? 'ro-cmp-negative' : 'ro-cmp-neutral'}>
                        {diff != null ? (
                          <>
                            <span className="ro-comparison-arrow">{diff < 0 ? '\u25BC' : diff > 0 ? '\u25B2' : ''}</span>
                            {`${diff < 0 ? '' : '+'}${diff.toFixed(1)}`}
                            <span className="ro-comparison-pct">
                              ({baseMiles ? `${((diff / baseMiles) * 100).toFixed(1)}%` : ''})
                            </span>
                          </>
                        ) : '\u2014'}
                      </td>
                    )}
                    <td style={{ textAlign: 'right' }}>{s.num_routes}</td>
                    <td style={{ textAlign: 'right' }}>{s.active_technicians}</td>
                    <td style={{ textAlign: 'right' }}>{s.total_stops}</td>
                    <td style={{ textAlign: 'right' }}>{s.elapsed_seconds ? formatElapsed(s.elapsed_seconds) : '-'}</td>
                    <td>{isBest ? <span className="ro-badge-best">BEST</span> : ''}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </>
      )}


      {/* Proceed to Review */}
      {onProceed && completedTiers.length > 0 && !optimizing && (
        <div className="ro-section-divider">
          <button className="ro-btn ro-btn-primary ro-btn-lg" onClick={onProceed}>
            Proceed to Review Results
          </button>
        </div>
      )}
    </div>
  );
}

const STAGE_MAP = [
  { match: /starting|loading/i, label: 'Starting', icon: '\u25B6' },
  { match: /building|matrix/i, label: 'Building distance matrix', icon: '\u25A3' },
  { match: /distribut|assign|analyz|constraint|feasib/i, label: 'Assigning addresses to days', icon: '\u25A4' },
  { match: /solv|optimiz|routing|scor|validat/i, label: 'Optimizing routes', icon: '\u25C8' },
  { match: /post.?process|improv|refin|geometr|road|osrm/i, label: 'Generating road maps', icon: '\u25CC' },
  { match: /upload|saving|complete|done/i, label: 'Saving results', icon: '\u25CF' },
];

// Track the highest stage reached so stages never go backwards
let _maxStageReached = 0;

function ProgressStages({ progress }) {
  const text = progress || 'Starting optimization...';
  const matchedStage = STAGE_MAP.findIndex(s => s.match.test(text));
  if (matchedStage >= 0 && matchedStage > _maxStageReached) {
    _maxStageReached = matchedStage;
  }
  // Never go backwards — use the highest stage we've seen
  const stage = matchedStage >= 0 ? Math.max(matchedStage, _maxStageReached) : _maxStageReached;

  return (
    <div className="ro-progress-stages">
      {STAGE_MAP.map((s, i) => {
        const isActive = i === stage;
        const isDone = i < stage;
        const cls = ['ro-progress-stage'];
        if (isActive) cls.push('ro-progress-stage-active');
        else if (isDone) cls.push('ro-progress-stage-done');
        return (
          <div key={i} className={cls.join(' ')}>
            <span className="ro-progress-stage-icon">
              {isDone ? '\u2713' : isActive ? s.icon : '\u25CB'}
            </span>
            <span>{s.label}{isActive ? '...' : ''}</span>
          </div>
        );
      })}
    </div>
  );
}
